import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  Container,
  Paper
} from '@mui/material';
import { CheckCircle, ErrorOutline, SmartToy } from '@mui/icons-material';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const token = searchParams.get('token');
        if (!token) throw new Error('Invalid token');

        await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/verify-email`,
          { token }
        );
        setStatus('success');
      } catch (err) {
        setStatus('error');
      }
    };

    verifyToken();
  }, [searchParams]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, #2c3e50 100%)`,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          zIndex: 1
        }}
      >
        {[...Array(6)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: ['100px', '150px', '200px'][i % 3],
              height: ['100px', '150px', '200px'][i % 3],
              background: `rgba(76, 133, 240, ${0.03 + (i % 3) * 0.02})`,
              borderRadius: '50%',
              top: `${10 + i * 15}%`,
              left: `${5 + i * 15}%`,
              animation: `float ${5 + i * 2}s infinite ease-in-out alternate`
            }}
          />
        ))}
      </Box>

      <Container
        maxWidth="sm"
        sx={{
          zIndex: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <Paper
          elevation={10}
          sx={{
            p: 4,
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
            background: 'rgba(255, 255, 255, 0.9)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}
        >
          <Box display="flex" justifyContent="center" mb={3}>
            <SmartToy
              sx={{
                color: theme.palette.primary.main,
                fontSize: '3rem',
                animation: 'bounce 2s infinite'
              }}
            />
          </Box>

          {status === 'loading' && (
            <>
              <CircularProgress />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Verifying your email...
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle
                sx={{
                  color: theme.palette.success.main,
                  fontSize: '3rem',
                  animation: 'fadeIn 1s ease-in-out'
                }}
              />
              <Typography variant="h4" sx={{ mt: 2, fontWeight: 'bold' }}>
                Email Verified Successfully!
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, color: 'text.secondary' }}>
                Your email has been verified. You can now log in to your account.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 3 }}
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <ErrorOutline
                sx={{
                  color: theme.palette.error.main,
                  fontSize: '3rem',
                  animation: 'shake 0.5s ease-in-out'
                }}
              />
              <Typography variant="h4" sx={{ mt: 2, fontWeight: 'bold' }}>
                Verification Failed
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, color: 'text.secondary' }}>
                The verification link is invalid or has expired. Please try again or contact support.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 3 }}
                onClick={() => navigate('/register')}
              >
                Register Again
              </Button>
            </>
          )}
        </Paper>
      </Container>

      {/* CSS for animations */}
      <style jsx global>{`
        @keyframes float {
          0% {
            transform: translateY(0) rotate(0deg);
          }
          100% {
            transform: translateY(-20px) rotate(10deg);
          }
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes shake {
          0%, 100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-5px);
          }
          75% {
            transform: translateX(5px);
          }
        }
      `}</style>
    </Box>
  );
};

export default VerifyEmail;
